import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, from, switchMap } from 'rxjs';
import type { PlatformAPI } from '@ol/kit';

// Marker header set by callers that talk to a non-backend origin (e.g. a
// presigned S3/CloudFront URL). Mirrors the standalone Auth0 interceptor's
// origin allow-list, which never reaches S3.
const SKIP_AUTH_HEADER = 'skipAuthHeader';

// Replaces @auth0/auth0-angular's AuthHttpInterceptor in embed mode: pulls the
// access token from the host platform instead of running its own OIDC client.
// Token freshness is the host's responsibility via getValidAccessToken().
@Injectable()
export class EmbedHttpAuthInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Presigned S3/CloudFront requests must NOT carry a bearer token — S3
    // rejects a presigned request that also has an Authorization header
    // (400 InvalidArgument), so the upload/download silently fails while the
    // resource row was already created (SH-15244). Strip the marker and pass
    // through untouched.
    if (request.headers.has(SKIP_AUTH_HEADER)) {
      return next.handle(request.clone({ headers: request.headers.delete(SKIP_AUTH_HEADER) }));
    }
    const api = (window as unknown as { __platform__?: PlatformAPI })['__platform__'];
    if (!api) return next.handle(request);
    return from(api.auth.getValidAccessToken()).pipe(
      switchMap((token) =>
        next.handle(
          token
            ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
            : request,
        ),
      ),
    );
  }
}
